import React from "react";

/**
 * Premium in-app button — the calmer counterpart to GlowButton (which is
 * reserved for hero CTAs because its rotating frame + fluid interior would
 * be exhausting on every dashboard button).
 *
 * Variants:
 *   - "primary"   gradient frame + dark interior, subtle hover glow.
 *                 Use for primary actions (Refresh, Sync, Secure Link).
 *   - "secondary" outline + faint fill, subtle hover.
 *                 Use for supporting actions (Upload, Add, Connect Wallet).
 *
 * Renders as <a> when `href` is set, otherwise <button>.
 */
const SIZES = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-4 py-2 text-sm",
  lg: "px-5 py-2.5 text-base",
};

const Button = React.forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    type = "button",
    href,
    onClick,
    disabled = false,
    className = "",
    children,
    ...rest
  },
  ref,
) {
  const isPrimary = variant === "primary";
  const sizeClass = SIZES[size] || SIZES.md;
  const dimClass = disabled ? "opacity-50 cursor-not-allowed" : "";

  const outerCls = isPrimary
    ? `glow-border inline-flex rounded-xl ${dimClass} ${className}`
    : `inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 dark:border-white/15 bg-white/60 dark:bg-white/[0.03] hover:bg-white dark:hover:bg-white/[0.07] hover:border-gray-400 dark:hover:border-white/25 text-gray-800 dark:text-gray-100 font-medium transition-colors ${sizeClass} ${dimClass} ${className}`;

  const wrapped = isPrimary ? (
    <span
      className={`flex items-center justify-center gap-2 rounded-xl bg-white dark:bg-[#0a0e16] text-gray-900 dark:text-white font-semibold w-full ${sizeClass}`}
    >
      {children}
    </span>
  ) : (
    children
  );

  const Tag = href ? "a" : "button";
  const extraProps = href ? { href } : { type, disabled };

  return (
    <Tag
      ref={ref}
      onClick={onClick}
      className={outerCls}
      {...extraProps}
      {...rest}
    >
      {wrapped}
    </Tag>
  );
});

export default Button;
